import { useState, useCallback } from "react";
import { useUser } from "../../context/userContext";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import Tooltip from "../Common/tooltip.component";
import { ReactComponent as DocIcon } from "../../assets/icons/file_icon.svg";
import FileIcon from "../../assets/icons/file_icon.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info_icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/download_icon.svg";
import { ReactComponent as ReplaceIcon } from "../../assets/icons/replace_icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete_icon.svg";
import { Link } from "react-router-dom";

const FileList = ({ activeSessionId, uploadedFiles, fetchData }) => {
  const {
    userData,
    handleGenerateSampleQuestions,
    isContentLoading,
    setIsContentLoading,
  } = useUser();
  const [selectedFiles, setSelectedFiles] = useState([]);

  console.log('uploadedFiles', uploadedFiles)

  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const filteredFiles = [];
    const allowedExtensions = [
      ".html",
      ".pdf",
      ".epub",
      ".rtf",
      ".odt",
      ".docx",
      ".txt",
      ".xlsx",
      ".xlsm",
      ".csv",
      ".ppt",
      ".md",
      ".pptx",
    ];
    console.log(acceptedFiles)
    const maxSize = 10 * 1024 * 1024; // 10MB

    for (const file of acceptedFiles) {
      const fileExtension = file.name.split(".").pop();
      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        alert(
          `The file type (${file.name}) that you are trying to upload is not allowed.`
        );
        continue;
      }
      // for (const existingFile of uploadedFiles[0]?.files) {
      //   if(file?.name === existingFile?.fileName){
      //     alert(
      //       `The file (${file.name}) already exists.`
      //     );
      //     continue;
      //   }
      // }
      if (file.size > maxSize) {
        alert(
          `The file size of ${file.name} exceeds the maximum allowed size (10MB).`
        );
        continue;
      }
      filteredFiles.push(file)
    }
    
    setSelectedFiles((prevFiles) => [...prevFiles, ...filteredFiles]);
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const uploadFile = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select at least one file.");
      return;
    }

    const isConfirmedUpload = window.confirm(
      `Are you sure you want to upload these files?`
    );

    if (!isConfirmedUpload) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("session_id", activeSessionId);
      formData.append("email", userData.email);

      selectedFiles.forEach((file) => {
        formData.append("file", file);
      });

      console.log("FormData:", formData);

      setUploading(true);

      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/append_session_content",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const progressPercent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(progressPercent);
          },
        }
      );

      console.log(response);

      const responseData = response.data.data;

      if (responseData && responseData.length > 0) {
        window.setTimeout(() => {
          fetchData();
        }, 0);
      }

      if (response.data.success) {
        await handleGenerateSampleQuestions(activeSessionId);
        fetchData();
      }
    } catch (error) {
      if (error.response && error.response.status === 413) {
        alert(
          "Something went wrong, unable to parse one or more file(s) you are trying to upload."
        );
      } else {
        alert("Error uploading file. Check if the file is not empty and try again!");
        console.error("Error uploading file:", error);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      setSelectedFiles([]);
    }
  };

  const handleDeleteFile = async (fileName, fileUri) => {
    if (uploadedFiles[0].files.length === 1) {
      alert(
        'Delete operation not permitted as each session requires at-least one file, try using the "Replace file" option instead!'
      );
      return;
    }

    const isConfirmed = window.confirm(
      `Are you sure you want to delete ${fileName}?`
    );

    if (!isConfirmed) {
      return;
    }

    setIsContentLoading(true);

    const formData = new FormData();
    formData.append("email", userData.email);
    formData.append("session_id", activeSessionId);
    formData.append("file_names", [fileName]);
    formData.append("file_urls", [fileUri]);

    try {
      const response = await axios.post(
        `https://soliboat.solidaridadasia.com/delete_selected_files`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
        }
      );

      console.log(response);

      if (response.data.success) {
        await handleGenerateSampleQuestions(activeSessionId);
        fetchData();
        alert(`Selected file (${fileName}) is deleted successfully.`);
      }
    } catch (error) {
      if (error.response && error.response.status === 413) {
        alert(
          "Something went wrong, unable to parse one or more file(s) you are trying to upload."
        );
      } else {
        alert("Error uploading file!");
        console.error("Error uploading file:", error);
      }
    } finally {
      setIsContentLoading(false);
    }
  };

  // Function to open file explorer for selecting multiple files
  const selectFiles = () => {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept =
        ".html,.pdf,.epub,.rtf,.odt,.docx,.txt,.xlsx,.xlsm,.csv,.ppt,.md";
      input.onchange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
          resolve(files);
        } else {
          reject(new Error("No files selected"));
        }
      };
      input.click();
    });
  };

  const handleReplaceFile = async () => {
    const isConfirmed = window.confirm(
      `Are you sure you want to replace all files?`
    );

    if (!isConfirmed) {
      return;
    }

    // Open file explorer for selecting multiple files
    const newFiles = await selectFiles();

    if (!newFiles || newFiles.length === 0) {
      // User canceled file selection or no files selected
      return;
    }

    // Validate files
    const allowedExtensions = [
      ".html",
      ".pdf",
      ".epub",
      ".rtf",
      ".odt",
      ".docx",
      ".txt",
      ".xlsx",
      ".xlsm",
      ".csv",
      ".ppt",
      ".md",
      ".pptx",
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    for (const file of newFiles) {
      const fileExtension = file.name.split(".").pop();
      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        alert(
          `The file type (${file.name}) that you are trying to upload is not allowed.`
        );
        return;
      }

      if (file.size > maxSize) {
        alert(
          `The file size of ${file.name} exceeds the maximum allowed size (10MB).`
        );
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("email", userData.email);
      formData.append("session_id", activeSessionId);

      // Initialize arrays for file names and URLs
      const fileNames = [];
      const fileUrls = [];

      // Map through uploaded files to extract file names and URLs
      uploadedFiles[0]?.files.forEach((file, fileIndex) => {
        fileNames.push(file.fileName);
        fileUrls.push(file.fileUri);
      });

      // Append arrays to formData
      formData.append("file_names", fileNames);
      formData.append("file_urls", fileUrls);

      // Append new files
      newFiles.forEach((file) => {
        formData.append("new_files", file);
      });

      setUploading(true);

      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/replace_file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const progressPercent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(progressPercent);
          },
        }
      );

      console.log(response);

      const responseData = response.data;

      if (responseData && responseData.success) {
        await handleGenerateSampleQuestions(activeSessionId);
        window.setTimeout(() => {
          alert(responseData.message[0]);
          fetchData();
        }, 0);
      }
    } catch (error) {
      if (error.response && error.response.status === 413) {
        alert(
          "Something went wrong, unable to parse one or more file(s) you are trying to upload."
        );
      } else {
        alert("Error uploading file!");
        console.error("Error uploading file:", error);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      setSelectedFiles([]);
    }
  };

  const clearButtonText =
    window.innerWidth > 768 ? "Clear Selected Files" : "Clear Files";
  const uploadButtonText =
    window.innerWidth > 768 ? "Upload Selected Files" : "Upload Files";

  return (
    <>
      {/* Initial Loader Screen */}
      {isContentLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-neutral-50 z-[100]">
          <div className="w-20 h-20 animate-spin">
            <div className="h-full w-full border-4 border-t-dark/50 border-b-dark/80 rounded-[50%]" />
          </div>
          <div className="text-center text-sm lg:text-xl font-medium text-gray-500 mt-8 mx-8 lg:mx-0">
            Please wait while while we process your request{" "}
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
          </div>
        </div>
      )}
      <div className="mt-4">
        <div className="rounded-lg">
          <div
            {...getRootProps({
              className:
                "flex items-center justify-center p-2 bg-neutral-50 border-dashed border-2 border-neutral-300 rounded-xl",
            })}
          >
            <input {...getInputProps()} />
            <div className="flex items-center">
              <img
                src={FileIcon}
                alt="File Icon"
                className="h-8 md:h-12 m-2 md:m-4"
              />
              <span>
                <p className="hidden md:block font-semibold text-neutral-700">
                  Drag & drop your file here
                </p>
                <div className="flex items-center">
                  <p className="ml-2 md:ml-0 text-sm md:text-base">
                    File formats we support.
                  </p>
                  <Tooltip
                    className="bottom-[105%] md:bottom-[120%] left-[-50%] md:left-[54%]"
                    width="240px"
                    content="Supported file types: .html, .pdf, .epub, .rtf, .odt, .docx, .txt, .xlsx, .xlsm, .csv, .ppt, .md"
                  >
                    <InfoIcon
                      className="w-5 h-5 ml-1 cursor-pointer"
                      fill="none"
                      stroke="#6495ED"
                    />
                  </Tooltip>
                </div>
              </span>
            </div>
            <div className="ml-3 md:ml-6">
              <button
                className="flex items-center px-2 md:px-6 py-2 bg-blue-50 border-2 border-blue-200 rounded"
                onClick={open}
              >
                <DownloadIcon className="w-5 h-5 p-[3px] border border-neutral-700 rounded-xl rotate-180" />
                <span className="ml-2 text-sm md:text-base font-medium text-neutral-700 hover:underline underline-offset-2">
                  Browse
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {uploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-50">
            <div className="relative flex flex-col bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <p className="text-slate-700 text-lg font-semibold">
                  Uploading ...
                </p>
                <p className="text-blue-700 text-lg font-semibold">
                  {progress}%
                </p>
              </div>
              <div className="w-48 h-4 bg-blue-200 mt-2 rounded-full">
                <div
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <InfoIcon className="w-6 h-6 mr-1" fill="white" />
              <p className="text-neutral-50">
                This might take a few moments ...
              </p>
            </div>
          </div>
        )}

        {/* Display selected files */}
        {selectedFiles.length > 0 && (
          <div className="mt-4 p-2 md:p-4 border border-neutral-300 rounded-xl">
            <div className="flex flex-col md:flex-row md:items-center justify-between md:justify-normal md:py-2">
              <div className="md:w-4/5">
                <p className="mt-1 ml-1 md:ml-2 text-neutral-600 text-lg font-medium md:font-semibold">
                  Selected Files:
                </p>
              </div>
              <div className="mt-2 md:mt-0 md:w-2/5 flex justify-between md:justify-end">
                <button
                  className="py-2 px-3 text-sm md:text-base font-medium md:font-semibold text-blue-500 border border-blue-100 rounded-lg hover:opacity-80 hover:underline"
                  onClick={() => setSelectedFiles([])}
                >
                  {clearButtonText}
                </button>
                <button
                  className="ml-3 px-6 py-2 text-sm md:text-base font-medium md:font-semibold bg-primary text-white rounded-md hover:scale-[102%] hover:shadow-md"
                  onClick={uploadFile}
                >
                  {uploadButtonText}
                </button>
              </div>
            </div>

            <div className="border-b border-neutral-100 mt-2 mb-2" />
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex justify-between items-center mb-2 px-2 md:px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-md"
              >
                <div className="w-full flex items-center pr-4">
                  <DocIcon className="min-w-[28px] w-7 md:w-8 h-7 md:h-8" />
                  <div className="flex items-center line-clamp-1">
                    <p className="ml-3 text-base md:text-lg text-blue-400 leading-tight line-clamp-1 cursor-pointer">
                      {file.name}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border border-neutral-100 my-2 md:my-4" />
        <div className="p-2 md:p-4 border border-neutral-200 rounded-xl">
          <div className="flex justify-between items-center">
            <h1 className="ml-1 md:ml-2 mt-1 md:my-3 lg:my-4 text-lg text-neutral-700 font-medium md:font-semibold">
              Uploaded file(s){" "}
              <span className="font-normal text-blue-500">
                ({uploadedFiles[0]?.files.length})
              </span>
            </h1>
            <Tooltip
              width="120px"
              content="Replace Files"
              className="bottom-[105%] left-[-50%] md:left-[54%]"
            >
              <button
                className="flex items-center ml-2 p-1 md:p-2 border border-neutral-400 rounded-lg cursor-pointer"
                onClick={handleReplaceFile}
              >
                <ReplaceIcon className="w-6 h-6 p-[3px] border border-neutral-700 rounded-full" />
                <span className="hidden md:block ml-2 hover:text-blue-700 hover:underline underline-offset-2">
                  Replace Files
                </span>
              </button>
            </Tooltip>
          </div>

          <div className="border-b border-neutral-100 my-2" />
          {uploadedFiles[0]?.files.map((file, fileIndex) => (
            <div
              key={fileIndex}
              className="flex justify-between items-center mb-2 p-2 md:p-4 bg-neutral-50 border border-neutral-200 rounded-md"
            >
              <div className="w-4/5 flex items-center pr-4 line-clamp-1">
                <DocIcon className="min-w-[28px] w-7 md:w-8 h-7 md:h-8" />
                <Link
                  to={file.fileUri}
                  title={file.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <p className="ml-3 text-base md:text-lg text-blue-400 leading-tight line-clamp-1 cursor-pointer">
                    {file.fileName}
                  </p>
                </Link>
              </div>
              <div className="w-1/5 flex justify-end">
                <Tooltip
                  className="bottom-[105%] left-[-50%] md:left-[54%]"
                  width="120px"
                  content="Download File"
                >
                  <div className="flex">
                    <Link
                      to={file.fileUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-1 md:p-2 border border-neutral-400 rounded-lg"
                    >
                      <DownloadIcon className="w-6 h-6 p-[3px] border border-neutral-700 rounded-full" />
                    </Link>
                  </div>
                </Tooltip>
                <Tooltip
                  className="bottom-[105%] left-[-50%] md:left-[54%]"
                  width="120px"
                  content="Delete File"
                >
                  <button
                    className="flex items-center ml-2 p-1 md:p-2 border border-neutral-400 rounded-lg cursor-pointer"
                    onClick={() =>
                      handleDeleteFile(file.fileName, file.fileUri)
                    }
                  >
                    <DeleteIcon className="w-6 h-6 p-[3px] border border-neutral-700 rounded-full" />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default FileList;
